import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { isCompletoTier } from "../utils/planFeatures";
import { Row, Col } from "react-bootstrap";
import ShowCustomerInfo from "./ShowCustomerInfo";
import { LuUser, LuPhone, LuPhoneOff, LuPhoneMissed, LuMail, LuMailCheck, LuMailPlus, LuMessageSquare, LuHistory, LuFileEdit, LuLock, LuStickyNote, LuMapPin } from "react-icons/lu";
import ReservationItems from "./Reservations/ReservationItems";
import ReservationChat from "./ReservationChat";
import ReservationHistory from "./ReservationHistory";
import Messages from ".//Messages";
import SwitchLabelButton from "./SwitchLabelButton";
import { postPhoneOrEmailConfirmation } from "../actions/reservationsActions";
import ReservationScreen from "../screens/ReservationScreen";
import { ModernButton } from "./DefaultImports";
import ReservationTableNameSelector from "./Reservations/ReservationTableNameSelector";
import ReservationStatusChanger from "./Reservations/ReservationStatusChanger";
import { ConfirmationModal } from "./ConfirmationModal";
import { customerAttendUpdateSlice } from "../reducers/restaurantReducers";
import { useSelector, useDispatch } from "react-redux";
import { CustomModal } from "./CustomModal";
import TableLocatorModal from "../place_design/TableLocatorModal";
import i18n from "../i18n";
const TOP_ICON_COLOR = "#607c76";

// Shared 28px action targets and 18px outline icons keep every header aligned.
const ICON_SIZE_CONFIG = {
  topHeight: 40,
  headerPadding: "0 8px",
  headerGap: 4,
  actionGap: 2
};
const CHAT_TRIGGER_ICON = <LuMessageSquare />;
const HISTORY_TRIGGER_ICON = <LuHistory />;
const getIconSize = (iconKey, sizeKey) => {
  return sizeKey === "icon" ? 18 : 28;
};
const getLayoutSize = key => {
  return ICON_SIZE_CONFIG?.[key];
};
const ReservationGrid = styled.div`
  display: grid;
  gap: ${({ $variant, $compact }) =>
    $variant === "floorBoard" ? "8px" : $compact ? "6px" : "10px"};
  width: 100%;
`;
const ReservationTop = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${({ $compact }) =>
    $compact ? "54px" : `${getLayoutSize("topHeight")}px`};
  background-color: #f0f5f3;
  border-bottom: 1px solid #e4ece8;
  color: ${TOP_ICON_COLOR};
  overflow: hidden;
`;
const ReservationHeaderRow = styled.div`
  width: 100%;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  padding: ${getLayoutSize("headerPadding")};
  gap: ${getLayoutSize("headerGap")}px;
`;
const ReservationNameButton = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  color: #344b46;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  margin: 0;
  cursor: inherit;
  overflow: hidden;
`;
const CustomerInfoIconButton = styled.button`
  width: ${getIconSize("customer", "box")}px;
  height: ${getIconSize("customer", "box")}px;
  min-width: ${getIconSize("customer", "box")}px;
  border: none;
  background: transparent;
  color: ${TOP_ICON_COLOR};
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 5px;
  box-sizing: border-box;

  svg {
    width: ${getIconSize("customer", "icon")}px !important;
    height: ${getIconSize("customer", "icon")}px !important;
    max-width: none !important;
    max-height: none !important;
    display: block;
    stroke-width: 1.75;
  }

  &:hover {
    background: #e1ebe6;
  }
`;
const ReservationNameText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 650;
  line-height: 1.25;
`;
const ReservationHeaderActions = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${getLayoutSize("actionGap")}px;
  overflow: visible;
`;
const DesktopTableSelectorSlot = styled.div`
  display: contents;
`;
const HeaderIconButton = styled.button`
  width: ${({
  $iconKey
}) => `${getIconSize($iconKey, "box")}px`};
  height: ${({
  $iconKey
}) => `${getIconSize($iconKey, "box")}px`};
  min-width: ${({
  $iconKey
}) => `${getIconSize($iconKey, "box")}px`};
  border: none;
  background: transparent;
  color: ${TOP_ICON_COLOR};
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 1;
  overflow: visible;
  border-radius: 5px;
  box-sizing: border-box;

  svg {
    width: ${({
  $iconKey
}) => `${getIconSize($iconKey, "icon")}px`} !important;
    height: ${({
  $iconKey
}) => `${getIconSize($iconKey, "icon")}px`} !important;
    max-width: none !important;
    max-height: none !important;
    display: block;
    color: ${TOP_ICON_COLOR};
    stroke-width: 1.75;
  }

  &:hover {
    background: #e1ebe6;
  }

  /* The daily plan is hidden on a phone, so the table locator only earns its
     place there. Kept on the same 900px breakpoint the plan itself uses. */
  ${({ $mobileOnly }) =>
    $mobileOnly &&
    `
      @media (min-width: 901px) {
        display: none;
      }
    `}
`;
const HeaderIconWrap = styled.div`
  width: ${({
  $iconKey
}) => `${getIconSize($iconKey, "box")}px`};
  height: ${({
  $iconKey
}) => `${getIconSize($iconKey, "box")}px`};
  min-width: ${({
  $iconKey
}) => `${getIconSize($iconKey, "box")}px`};
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${TOP_ICON_COLOR};
  overflow: visible;
  border-radius: 5px;
  box-sizing: border-box;

  & > * {
    width: 100%;
    height: 100%;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }

  button,
  a {
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    color: ${TOP_ICON_COLOR} !important;
  }

  svg {
    width: ${({
  $iconKey
}) => `${getIconSize($iconKey, "icon")}px`} !important;
    height: ${({
  $iconKey
}) => `${getIconSize($iconKey, "icon")}px`} !important;
    max-width: none !important;
    max-height: none !important;
    display: block;
    color: ${TOP_ICON_COLOR};
    stroke-width: 1.75;
    filter: none;
  }

  &:hover {
    background: #e1ebe6;
  }
`;
const ReservationCardStyle = styled.div`
  display: ${({ $compact }) => ($compact ? "grid" : "flex")};
  grid-template-columns: ${({ $compact }) =>
    $compact ? "minmax(300px, 0.9fr) minmax(0, 1.7fr)" : "none"};
  align-items: ${({ $compact }) => ($compact ? "stretch" : "initial")};
  flex-wrap: wrap;
  background-color: white;
  border: ${({
  $variant
}) => $variant === "floorBoard" ? "none" : "1px solid #e5e0d7"};
  border-left: ${({
  $selected,
  $variant
}) => {
  if ($variant !== "floorBoard") {
    return $selected ? "5px solid #ee754c" : "1px solid #e5e0d7";
  }
  return $selected ? "5px solid #ee754c" : "3px solid transparent";
}};
  border-radius: ${({ $variant, $compact }) =>
    $variant === "floorBoard" ? "0" : $compact ? "10px" : "12px"};
  padding: 0;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  max-height: ${({
  $variant
}) => $variant === "floorBoard" ? "none" : "400px"};
  margin: 0;
  cursor: ${({
  $variant
}) => $variant === "floorBoard" ? "pointer" : "default"};
  box-shadow: ${({ $variant, $compact }) =>
    $variant === "floorBoard" || $compact
      ? "none"
      : "0 3px 12px rgba(75, 51, 53, 0.06)"};
  transition:
    border-left-color 0.15s ease,
    border-left-width 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-left-color: ${({
  $variant
}) => $variant === "floorBoard" ? "#ee754c" : "#e5e0d7"};
    background-color: ${({
  $variant
}) => $variant === "floorBoard" ? "#fffaf7" : "white"};
    box-shadow: ${({ $variant, $compact }) =>
      $variant === "floorBoard" || $compact
        ? "none"
        : "0 8px 22px rgba(75, 51, 53, 0.09)"};
  }

  ${({ $compact }) =>
    $compact &&
    `
      min-height: 54px;

      > .row {
        display: grid !important;
        grid-template-columns: minmax(0, 1fr) minmax(220px, 310px);
        align-items: center;
        min-width: 0;
        height: 100%;
        padding-right: 54px !important;
      }

      > .row > .col-12 {
        width: auto !important;
        min-width: 0;
      }

      @media (max-width: 1100px) {
        grid-template-columns: minmax(270px, 0.85fr) minmax(0, 1.4fr);

        > .row {
          grid-template-columns: minmax(0, 1fr) minmax(190px, 240px);
        }
      }

      @media (max-width: 768px) {
        grid-template-columns: minmax(0, 1fr);

        > div:first-child {
          height: 42px;
        }

        > .row {
          min-height: 0;
          grid-template-columns: minmax(0, 1fr) 46px;
          padding: 0 !important;
          align-items: stretch;
        }

        > .row > .col-12:first-child {
          grid-column: 1 / -1;
        }

        > .row > .col-12:nth-child(2) {
          grid-column: 1;
        }
      }
    `}

  ${({ $notification }) => $notification && `
    /* The bell is a narrow panel even on a wide desktop screen. */
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
    max-height: none;

    > div:first-child {
      height: 40px;
    }

    > .row {
      grid-template-columns: minmax(0, 1fr);
      padding: 0 !important;
      height: auto;
    }

    > .row > .col-12 {
      grid-column: 1 / -1;
    }

    > .row > .col-12:last-child {
      border-top: 1px solid #e7eceb;
    }
  `}

  ${({ $variant }) => $variant === "floorBoard" && `
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
    max-height: none;
    border: 1px solid #e4ebe9;
    border-left: 3px solid transparent;
    border-radius: 8px;
    > div:first-child { height: auto; min-height: 32px; }
    > .row {
      grid-template-columns: minmax(0, 1fr) 46px;
      padding: 0 !important;
      height: auto;
      align-items: stretch;
    }
    > .row > .col-12:first-child { grid-column: 1 / -1; }
    > .row > .col-12:nth-child(2) { grid-column: 1; }
    @media (max-width: 1100px) {
      grid-template-columns: minmax(0, 1fr);
      > .row { grid-template-columns: minmax(0, 1fr) 46px; }
    }
  `}
  ${({ $variant, $selected }) => $variant === "floorBoard" && $selected && `
    border-left-color: #ee754c;
  `}
  ${({ $variant }) => $variant === "floorBoard" && `
    /* One line, never two. A wrapping header row pushed the whole action strip
       onto a second line where it was clipped out of sight, and let the guest
       name break mid-word. The name shrinks to an ellipsis instead, and the
       icons shrink enough that six of them fit a 330px card. */
    ${ReservationHeaderRow} { flex-wrap: nowrap; padding: 3px 7px; gap: 2px; }
    ${ReservationNameButton} { flex: 1 1 0; min-width: 0; gap: 4px; }
    ${ReservationNameText} {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    ${ReservationHeaderActions} { margin-left: auto; flex: 0 0 auto; gap: 1px; }
    ${HeaderIconButton}, ${HeaderIconWrap} {
      width: 24px;
      height: 24px;
      min-width: 24px;
    }
    ${HeaderIconButton} svg, ${HeaderIconWrap} svg {
      width: 15px !important;
      height: 15px !important;
    }
    ${CustomerInfoIconButton} { width: 22px; height: 22px; min-width: 22px; }
    ${CustomerInfoIconButton} svg { width: 15px !important; height: 15px !important; }
  `}
`;
const ReservationCard = React.memo(({
  reservations,
  reservations_complete,
  isShowCustomerInfo = false,
  isNotifications = false,
  isReservationFromRestaurantScreen = false,
  dispatch_reservation_wait = 0,
  past_estimated_time = false,
  variant = "default",
  selectedReservationId = null,
  onReservationSelect
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isShowCustomerInfoModalActive, setIsShowCustomerInfoModalActive] = useState(false);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [tokens, setTokens] = useState("");
  const [reservation_or_waiting_list, setReservation_or_waiting_list] = useState("reservation");
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [activeRestaurantNote, setActiveRestaurantNote] = useState(null);
  // Which booking the "where is my table" plan is open for, if any.
  const [locatorReservation, setLocatorReservation] = useState(null);
  const [phoneIsConfirmed, setPhoneIsConfirmed] = useState(false);
  const [completePhone, setCompletePhone] = useState("");
  const [current_reservation_id, setCurrent_reservation_id] = useState("");
  const [customer_name, setCustomer_name] = useState("");
  const manager_inf = useSelector(state => state.crudManager);
  const {
    managerInfo
  } = manager_inf;
  const canEditReservation =
    !managerInfo ||
    managerInfo.permissions === "full_access" ||
    Boolean(managerInfo.edit_reservations);
  const reservationsFromRestaurant = useSelector(state => state.reservationsFromRestaurant);
  const {
    error
  } = reservationsFromRestaurant;
  const customerAttendUpdates = useSelector(state => state.customerAttendUpdate);
  const {
    errorChangeTableNumber
  } = customerAttendUpdates;
  const restaurant_info = useSelector(state => state.restaurantDetails);
  const {
    restaurants
  } = restaurant_info;
  const reservationZone = useSelector(state => state.reservationZone);
  const {
    zones
  } = reservationZone;
  const all_tables = useSelector(state => state.restaurantTables.tables);
  const isFloorBoardVariant = variant === "floorBoard";
  // The client history uses the same compact card as the reservations screen.
  // That variant is what puts the table-number button in the status row, tightens
  // the grid and shortens the header — the design, in other words. It does not
  // bring back the phone and e-mail actions, which stay gated on
  // !isShowCustomerInfo further down.
  const isCompactCard =
    isReservationFromRestaurantScreen ||
    isNotifications ||
    isFloorBoardVariant ||
    isShowCustomerInfo;

  /*
    Normal reservation screen:
      isReservationFromRestaurantScreen === true
      => do NOT show before/after history fields inside ReservationItems.
      History/activity usage:
      isReservationFromRestaurantScreen === false
      => ReservationItems can show before/after fields.
  */
  const shouldShowHistoryFields = !isReservationFromRestaurantScreen && !isNotifications;
  const reservationCardRefs = useRef({});
  useEffect(() => {
    if (!isFloorBoardVariant || !selectedReservationId) return;
    const element = reservationCardRefs.current[String(selectedReservationId)];
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  }, [isFloorBoardVariant, selectedReservationId]);
  const [reservationTokenToPhoneLink, setReservationTokenToPhoneLink] = useState("");
  const [reservationHasBeenModified, setReservationHasBeenModified] = useState(false);
  const [modalReservationModifyReservation, setModalReservationModifyReservation] = useState(false);
  const handleCardSelect = (event, reservation) => {
    if (!isFloorBoardVariant) return;
    const interactiveTarget = event.target.closest('button, a, input, select, textarea, label, [role="button"], [data-prevent-card-select="true"]');
    if (interactiveTarget) return;
    if (typeof onReservationSelect === "function") {
      onReservationSelect(reservation);
    }
  };
  const handleSetPhoneNumber = (country_code, phone_number, reservationID, isPhoneConfirmed, the_customer_name, reservation_token) => {
    const countryCode = country_code || "";
    const phoneNumber = phone_number || "";
    const countryCodeLength = countryCode.length;
    const formattedPhoneNumber = phoneNumber.startsWith(countryCode) ? countryCode + " " + phoneNumber.slice(countryCodeLength) : phoneNumber;
    setCompletePhone(formattedPhoneNumber);
    setShowPhoneModal(!showPhoneModal);
    setCurrent_reservation_id(reservationID);
    setPhoneIsConfirmed(isPhoneConfirmed);
    setCustomer_name(the_customer_name);
    setReservationTokenToPhoneLink(reservation_token);
  };
  const UpdatePhoneNumber = () => {
    dispatch(postPhoneOrEmailConfirmation(current_reservation_id, "phone"));
  };
  const showCustomerInfo = (country_code, phone_number) => {
    setCompletePhone(phone_number || "");
    setIsShowCustomerInfoModalActive(true);
  };
  const handleOpenEditReservationModal = tokeni => {
    setIsReservationModalOpen(true);
    setIsCreatingReservation(false);
    setReservation_or_waiting_list("reservation");
    setTokens(tokeni);
  };
  const handleOpenEmailEdit = reservation_token => {
    handleOpenEditReservationModal(reservation_token);
  };
  const handleCloseReservationModal = () => {
    setIsReservationModalOpen(false);
    setTokens();
  };
  return <ReservationGrid $variant={variant} $compact={isCompactCard}>
        {reservations && reservations.length > 0 && restaurants ? reservations.map(reservation => {
      const isSelectedInFloorBoard = isFloorBoardVariant && Number(selectedReservationId) === Number(reservation.id);
      return <ReservationCardStyle key={reservation.id} $variant={variant} $compact={isCompactCard} $notification={isNotifications} $selected={isSelectedInFloorBoard} ref={element => {
        if (element) {
          reservationCardRefs.current[String(reservation.id)] = element;
        }
      }} onClick={event => handleCardSelect(event, reservation)}>
                <ReservationTop status={reservation.status} $variant={variant} $compact={isCompactCard} $selected={isSelectedInFloorBoard}>
                  <ReservationHeaderRow $variant={variant}>
                    {String(reservation?.restaurant_note || "").trim() && (
                      <HeaderIconButton
                        type="button"
                        $iconKey="note"
                        data-reservation-action="restaurant-note"
                        title={reservation.restaurant_note}
                        aria-label={t("reservations.restaurantNote", { defaultValue: "Nota do restaurante" })}
                        onClick={event => {
                          event.stopPropagation();
                          setActiveRestaurantNote(reservation.restaurant_note);
                        }}
                      >
                        <LuStickyNote />
                      </HeaderIconButton>
                    )}
                    <ReservationNameButton>
                      <CustomerInfoIconButton type="button" title={i18n.t("ui.components.reservationCard.verInformacaoDoCliente")} onClick={event => {
                event.stopPropagation();
                showCustomerInfo(reservation.country_code, reservation.phone_number);
              }}>
                        <LuUser />
                      </CustomerInfoIconButton>

                      <ReservationNameText>
                        {reservation.full_name}
                      </ReservationNameText>
                    </ReservationNameButton>

                    <ReservationHeaderActions>
                      {!isShowCustomerInfo && <>
                          {!reservation.phone_number || reservation.phone_number.trim() === "" || reservation.phone_number.trim().length <= 3 ? <HeaderIconButton type="button" title={i18n.t("ui.components.reservationCard.semNumeroDeTelefone")} $iconKey="phone" onClick={event => {
                  event.stopPropagation();
                  handleSetPhoneNumber(reservation.country_code, reservation.phone_number, reservation.id, reservation.is_phone_confirmed, reservation.full_name, reservation.token);
                }}>
                              <LuPhoneOff />
                            </HeaderIconButton> : reservation.is_phone_confirmed ? <HeaderIconButton type="button" title={i18n.t("ui.components.reservationCard.telefoneConfirmado")} $iconKey="phone" onClick={event => {
                  event.stopPropagation();
                  handleSetPhoneNumber(reservation.country_code, reservation.phone_number, reservation.id, reservation.is_phone_confirmed, reservation.full_name, reservation.token);
                }}>
                              <LuPhone />
                            </HeaderIconButton> : <HeaderIconButton type="button" title={i18n.t("ui.components.reservationCard.telefoneNaoConfirmado")} $iconKey="phone" onClick={event => {
                  event.stopPropagation();
                  handleSetPhoneNumber(reservation.country_code, reservation.phone_number, reservation.id, reservation.is_phone_confirmed, reservation.full_name, reservation.token);
                }}>
                              <LuPhoneMissed />
                            </HeaderIconButton>}

                          {!reservation.email || reservation.email.trim() === "" ? <HeaderIconButton type="button" title={i18n.t("ui.components.reservationCard.adicionarEmail")} $iconKey="email" onClick={event => {
                  event.stopPropagation();
                  handleOpenEmailEdit(reservation.token);
                }}>
                              <LuMailPlus />
                            </HeaderIconButton> : reservation.is_email_confirmed ? <HeaderIconButton type="button" title={i18n.t("ui.components.reservationCard.editarEmail")} $iconKey="email" onClick={event => {
                  event.stopPropagation();
                  handleOpenEmailEdit(reservation.token);
                }}>
                              <LuMailCheck />
                            </HeaderIconButton> : <HeaderIconButton type="button" title={i18n.t("ui.components.reservationCard.editarEmailNaoConfirmado")} $iconKey="email" onClick={event => {
                  event.stopPropagation();
                  handleOpenEmailEdit(reservation.token);
                }}>
                              <LuMail />
                            </HeaderIconButton>}
                        </>}

                      {String(reservation?.tablesused || "").trim() && (
                        <HeaderIconButton
                          type="button"
                          $iconKey="locate"
                          $mobileOnly
                          data-reservation-action="locate-table"
                          title={i18n.t("ui.tableLocator.title", { defaultValue: "Onde fica a mesa" })}
                          aria-label={i18n.t("ui.tableLocator.title", { defaultValue: "Onde fica a mesa" })}
                          onClick={event => {
                            event.stopPropagation();
                            setLocatorReservation(reservation);
                          }}
                        >
                          <LuMapPin />
                        </HeaderIconButton>
                      )}

                      <HeaderIconWrap $iconKey="chat" onClick={event => event.stopPropagation()} data-prevent-card-select="true">
                        <ReservationChat reservation={reservation} triggerIcon={CHAT_TRIGGER_ICON} />
                      </HeaderIconWrap>

                      {zones && <HeaderIconWrap $iconKey="history" onClick={event => event.stopPropagation()} data-prevent-card-select="true">
                          <ReservationHistory triggerIcon={HISTORY_TRIGGER_ICON} reservationHistory={reservation.history} timezone={restaurants.timezone} all_tables={all_tables} zones_with_id={zones.zones_with_id} isActivityBlocked={!restaurants || !isCompletoTier(restaurants?.sub_lvl)} restaurantId={restaurants?.id} />
                        </HeaderIconWrap>}

                      {canEditReservation ? <HeaderIconButton type="button" title={i18n.t("ui.components.reservationCard.editarReserva")} $iconKey="edit" onClick={event => {
                event.stopPropagation();
                handleOpenEditReservationModal(reservation.token);
              }}>
                          <LuFileEdit />
                        </HeaderIconButton> : <HeaderIconButton type="button" title={i18n.t("ui.components.reservationCard.reservaBloqueada")} $iconKey="lock" onClick={event => event.stopPropagation()}>
                          <LuLock />
                        </HeaderIconButton>}

                    </ReservationHeaderActions>
                  </ReservationHeaderRow>
                </ReservationTop>

                <Row style={{
          width: "100%",
          padding: "0",
          margin: "0"
        }}>
                  <Col xs={12} md={isNotifications ? 12 : isShowCustomerInfo ? 12 : 7} style={{
            width: "100%",
            padding: "0",
            margin: "0"
          }}>
                    <ReservationItems tableCountOnly compact={isShowCustomerInfo} reservation={reservation} all_tables={all_tables} previousReservationFromHistory={shouldShowHistoryFields} isReservationFromRestaurantScreen={isReservationFromRestaurantScreen} isNotifications={isNotifications} />
                  </Col>

                  <Col xs={12} style={{
            width: "100%",
            padding: "0",
            margin: "0"
          }}>
                    <ReservationStatusChanger compact={isCompactCard} narrow={isFloorBoardVariant} isNotifications={isNotifications} past_estimated_time={past_estimated_time} reservation={reservation} index={reservation.id} setTokens={setTokens} isShowCustomerInfo={isShowCustomerInfo} />
                  </Col>

                  {!isNotifications && (isCompactCard ? (
                    <DesktopTableSelectorSlot>
                      <ReservationTableNameSelector
                        compact
                        inline={isFloorBoardVariant}
                        reservation={reservation}
                        reservations={reservations_complete}
                        dispatch_reservation_wait={dispatch_reservation_wait}
                      />
                    </DesktopTableSelectorSlot>
                  ) : (
                    <ReservationTableNameSelector
                      reservation={reservation}
                      reservations={reservations_complete}
                      dispatch_reservation_wait={dispatch_reservation_wait}
                    />
                  ))}
                </Row>

              </ReservationCardStyle>;
    }) : error ? <Messages variant="error">{error}</Messages> : <p>{i18n.t("ui.components.reservationCard.naoFoiEncontradaNenhumaReservaPara")}</p>}

        <CustomModal show={showPhoneModal} onHide={() => setShowPhoneModal(false)} title={i18n.t("ui.components.reservationCard.telefonarParaCliente")} centered={false} footer={<>
              {completePhone.length > 4 ? <a href={`tel:${"+" + completePhone}`} style={{
        cursor: "pointer",
        textDecoration: "none"
      }}>
                  <ModernButton color="#99B0AD" fontSize="14px">{i18n.t("ui.components.reservationCard.telefonar")}</ModernButton>
                </a> : <ModernButton color="#99B0AD" fontSize="14px" style={{
        border: "none"
      }} disabled={true}>{i18n.t("ui.components.reservationCard.telefonar2")}</ModernButton>}

              <ModernButton color="#EE754C" fontSize="14px" onClick={() => handleOpenEditReservationModal(reservationTokenToPhoneLink)}>{i18n.t("ui.components.reservationCard.editarNumeroDeTelemovel")}</ModernButton>
            </>}>
          <div style={{
        paddingTop: "5px"
      }}>
            <div style={{
          fontSize: "20px",
          fontWeight: "600",
          marginBottom: "12px",
          color: "#9FBBC1",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "8px"
        }}>
              <span>{customer_name}</span>
              {completePhone.length > 4 && <span>
                  +{completePhone.slice(0, 3)} {completePhone.slice(3)}
                </span>}
            </div>

            {completePhone.length <= 4 && <div style={{
          fontSize: "20px",
          fontWeight: "600",
          color: "#999",
          marginBottom: "12px"
        }}>{i18n.t("ui.components.reservationCard.semNumeroDeTelemovelAssociado")}</div>}

            {completePhone.length > 4 && <div style={{
          marginTop: "5px"
        }}>
                <SwitchLabelButton leftText={t("ui.i18nFinalCleanup.notConfirmed")} rightText={t("ui.i18nFinalCleanup.confirmed")} onToggle={UpdatePhoneNumber} defaultState={phoneIsConfirmed} />
              </div>}
          </div>
        </CustomModal>

        <CustomModal
          show={activeRestaurantNote !== null}
          onHide={() => setActiveRestaurantNote(null)}
          title={t("reservations.restaurantNote", { defaultValue: "Nota do restaurante" })}
        >
          <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", margin: 0 }}>
            {activeRestaurantNote}
          </p>
        </CustomModal>

        <TableLocatorModal
          show={locatorReservation !== null}
          onHide={() => setLocatorReservation(null)}
          reservation={locatorReservation}
          allTables={all_tables}
          restaurantId={restaurants?.id}
        />

        {!isShowCustomerInfo && restaurants?.id && <ShowCustomerInfo phone_number={completePhone} restaurant_id={restaurants.id} isModalActive={isShowCustomerInfoModalActive} setIsModalActive={setIsShowCustomerInfoModalActive} />}

        {isReservationModalOpen && restaurants?.id && <CustomModal show={isReservationModalOpen} scrollable wide onHide={() => {
      if (reservationHasBeenModified) {
        setModalReservationModifyReservation(true);
      } else {
        setIsReservationModalOpen(false);
      }
    }} title={isCreatingReservation ? t("reservations.newReservation") : t("ui.components.reservationCard.editarReserva")} centered={false} body={<ReservationScreen from_restaurant={true} creating={isCreatingReservation} setCreating={setIsCreatingReservation} isEditModalOpen={isReservationModalOpen} setIsEditModalOpen={setIsReservationModalOpen} restaurantId={restaurants.id} handleCloseReservationModal={handleCloseReservationModal} token_from_restaurant={tokens} setTokens={setTokens} reservation_or_waiting_list={reservation_or_waiting_list} setReservationHasBeenModified={setReservationHasBeenModified} reservationHasBeenModified={reservationHasBeenModified} modalReservationModifyReservation={modalReservationModifyReservation} setModalReservationModifyReservation={setModalReservationModifyReservation} no_border={true} />} />}

        <ConfirmationModal show={errorChangeTableNumber} onHide={() => {
      dispatch(customerAttendUpdateSlice.actions.resetChangeTableNumber());
    }} title={i18n.t("ui.components.reservationCard.erro")} body={<>{i18n.t("ui.components.reservationCard.porFavorTentaNovamente")}</>} confirmLabel={<>{i18n.t("ui.components.reservationCard.ok")}</>} confirmColor="#EE754C" onConfirm={() => {
      dispatch(customerAttendUpdateSlice.actions.resetChangeTableNumber());
    }} />
      </ReservationGrid>;
});
export default ReservationCard;
